export type SharePayload = { title: string; text: string; url: string };

function wrapLines(context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let current = "";
  for (const character of text.replace(/\n+/gu, " ")) {
    const next = current + character;
    if (current && context.measureText(next).width > maxWidth) { lines.push(current); current = character; }
    else current = next;
  }
  if (current) lines.push(current);
  return lines.slice(0, 7);
}

async function createShareCard(payload: SharePayload): Promise<File | null> {
  if (typeof document === "undefined" || typeof File === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = 1080; canvas.height = 1350;
  const context = canvas.getContext("2d");
  if (!context) return null;
  const gradient = context.createLinearGradient(0, 0, 1080, 1350);
  gradient.addColorStop(0, "#071f42"); gradient.addColorStop(0.58, "#0b3568"); gradient.addColorStop(1, "#f5efe1");
  context.fillStyle = gradient; context.fillRect(0, 0, 1080, 1350);
  context.fillStyle = "rgba(255,255,255,.96)"; context.beginPath(); context.roundRect(70, 170, 940, 980, 42); context.fill();
  context.fillStyle = "#bf8a2b"; context.font = "700 34px system-ui, sans-serif"; context.fillText("TOINOWA", 120, 250);
  context.fillStyle = "#122342"; context.font = "700 56px system-ui, sans-serif";
  const body = payload.text.split("\n")[0] || payload.title;
  wrapLines(context, body, 820).forEach((line, index) => context.fillText(line, 120, 390 + index * 82));
  context.strokeStyle = "#d2aa58"; context.lineWidth = 4; context.beginPath(); context.moveTo(120, 1010); context.lineTo(960, 1010); context.stroke();
  context.fillStyle = "#38506f"; context.font = "600 30px system-ui, sans-serif"; context.fillText("問いで、世界をひらく。", 120, 1080);
  context.fillStyle = "#ffffff"; context.font = "600 34px system-ui, sans-serif"; context.fillText("問いを届ける → 3秒回答 → Reveal → 変化を共有", 75, 1245);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  return blob ? new File([blob], "toinowa-card.png", { type: "image/png" }) : null;
}

export function questionShareUrl(questionId: string, origin: string): string {
  const url = new URL(`/q/${encodeURIComponent(questionId)}`, origin);
  url.searchParams.set("ref", "share");
  return url.toString();
}

export function questionSharePayload(question: { id: string; body: string }, origin: string): SharePayload {
  return {
    title: "TOINOWAの問い",
    text: `${question.body}\n答えを見る前に、まず自分の答えを。`,
    url: questionShareUrl(question.id, origin),
  };
}

export function answerSharePayload(
  question: { id: string; body: string },
  answerLabel: string,
  origin: string,
): SharePayload {
  return {
    title: "私はこう答えました｜TOINOWA",
    text: `${question.body}\n\n私の答え：${answerLabel}\nあなたなら、どう答えますか？`,
    url: questionShareUrl(question.id, origin),
  };
}

export function changeSharePayload(
  question: { id: string; body: string },
  firstLabel: string,
  currentLabel: string,
  origin: string,
): SharePayload {
  return {
    title: "考えの変化｜TOINOWA",
    text: `${question.body}\n\n最初：${firstLabel}\nいま：${currentLabel}\n考えが変わることも、変わらないことも記録する。`,
    url: questionShareUrl(question.id, origin),
  };
}

function recordShare(url: string): void {
  void fetch("/api/events", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      eventName: "share",
      questionId: url.match(/\/q\/([0-9a-f-]+)/i)?.[1] || null,
    }),
  }).catch(() => undefined);
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("クリップボードへコピーできませんでした");
}

/** 回答内容や回答者情報を含まない、問いだけの共有URLをコピーします。 */
export async function copyQuestionShareLink(questionId: string, origin: string): Promise<string> {
  const url = questionShareUrl(questionId, origin);
  await copyText(url);
  recordShare(url);
  return url;
}

export async function shareOrCopy(payload: SharePayload): Promise<"shared" | "copied" | "cancelled"> {
  recordShare(payload.url);
  if (navigator.share) {
    try {
      const card = await createShareCard(payload);
      const shareData: ShareData = card && navigator.canShare?.({ files: [card] }) ? { ...payload, files: [card] } : payload;
      await navigator.share(shareData);
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
    }
  }
  const text = `${payload.title}\n${payload.text}\n${payload.url}`;
  await copyText(text);
  return "copied";
}
